import {
  TextSelect as UIKitTextSelect,
  type TextSelectOption,
  type TextSelectProps,
} from "@circle-react-shared/TextSelect/TextSelect";
import {
  Select as CompassSelect,
  type SelectProps as CompassSelectProps,
} from "@circleco/compass/components/Select";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import { omit } from "lodash";
import { type ReactNode, useMemo } from "react";

import { t } from "@/i18n-js/instance";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Re-export for compatibility
export type { TextSelectOption, TextSelectProps };

interface SelectOption<V = string> {
  label: string;
  suffix?: ReactNode;
  value: V;
}

const COMPASS_INJECTED_KEYS = [
  "horizontalLayoutRatio",
  "layout",
  "rootProps",
  "searchable",
] as const;

interface CompassTextSelectProps {
  horizontalLayoutRatio?: "25/75" | "30/70" | "33/67" | "40/60" | "50/50";
  layout?: "horizontal" | "vertical";
  rootProps?: Record<string, unknown>;
  searchable?: boolean;
}

interface SelectGroup<V = string> {
  items: SelectOption<V>[];
  label: string;
}

const toCompassItem = <Option extends TextSelectOption>(
  opt: Option,
): SelectOption<string> => ({
  value: String(opt.value),
  label: opt.label,
  suffix: opt.suffix,
});

const toCompassOptions = <Option extends TextSelectOption>(
  options: readonly Option[],
  groupBy?: (option: Option) => string | undefined,
): SelectOption<string>[] | SelectGroup<string>[] => {
  if (!groupBy) {
    return options.map(toCompassItem);
  }

  const groups = new Map<string, SelectOption<string>[]>();
  const ungrouped: SelectOption<string>[] = [];

  for (const opt of options) {
    const item = toCompassItem(opt);
    const group = groupBy(opt);

    if (group) {
      const bucket = groups.get(group);
      if (bucket) {
        bucket.push(item);
      } else {
        groups.set(group, [item]);
      }
    } else {
      ungrouped.push(item);
    }
  }

  const result: SelectGroup<string>[] = [];

  if (ungrouped.length > 0) {
    result.push({ label: "", items: ungrouped });
  }

  for (const [label, items] of groups) {
    result.push({ label, items });
  }

  return result;
};

export function TextSelect<Option extends TextSelectOption = TextSelectOption>(
  props: Readonly<TextSelectProps<Option> & CompassTextSelectProps>,
) {
  const isCompassEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_form_elements_enabled",
  );

  const {
    options = [],
    value,
    initialValue,
    onChange,
    onClear,
    onSearchChange,
    placeholder = t("select_an_option"),
    emptyMessage = t("no_options"),
    label,
    isError,
    disabled: isDisabled,
    groupBy,
    createLinkLabel,
    onCreateLinkClick,
    horizontalLayoutRatio,
    layout,
    rootProps,
    searchable: isSearchable = true,
  } = props;

  const compassOptions = useMemo(
    () => toCompassOptions(options, groupBy),
    [options, groupBy],
  );
  const compassOptionByValue = useMemo(
    () => new Map(options.map(opt => [String(opt.value), toCompassItem(opt)])),
    [options],
  );

  const resolvedValue = value === undefined ? initialValue : value;
  const selectedOption = useMemo(() => {
    if (resolvedValue == null) return null;
    return compassOptionByValue.get(String(resolvedValue)) ?? null;
  }, [compassOptionByValue, resolvedValue]);

  if (!isCompassEnabled) {
    return <UIKitTextSelect<Option> {...omit(props, COMPASS_INJECTED_KEYS)} />;
  }

  const compassLayout: "horizontal" | undefined =
    layout === "horizontal" ? "horizontal" : undefined;

  const handleValueChange = (newOption: SelectOption<string> | null) => {
    if (!onChange) return;

    if (newOption === null) {
      onClear?.();
      return;
    }

    const originalOption = options.find(
      opt => String(opt.value) === newOption.value,
    );
    onChange(originalOption?.value ?? newOption.value);
  };

  const creatableProps =
    createLinkLabel && onCreateLinkClick
      ? {
          creatable: true as const,
          onCreateOption: (inputValue: string) => onCreateLinkClick(inputValue),
          i18n: { creatableLabel: createLinkLabel },
        }
      : {};

  const compassProps: CompassSelectProps<string> = {
    rootProps,
    searchable: isSearchable,
    options: compassOptions,
    placeholder,
    emptyMessage,
    label,
    layout: compassLayout,
    horizontalLayoutRatio,
    disabled: isDisabled,
    invalid: isError,
    value: selectedOption,
    onValueChange: handleValueChange,
    onInputValueChange: onSearchChange,
    ...(layout === "horizontal" && horizontalLayoutRatio
      ? { horizontalLayoutRatio }
      : {}),
    isItemEqualToValue: (item, selected) => item.value === selected.value,
    ...creatableProps,
  };

  return (
    <CompassSelect
      {...compassProps}
      className={allowedClassesFor("Select", compassProps.className ?? "")}
    />
  );
}

TextSelect.displayName = "TextSelect";
